/**
 * FeedbackModal — the report form.
 *
 *  1. Pick one of four type tiles (Bug / Data integrity / Suggestion / Feature).
 *  2. Describe it (a title is derived server-side from the first line).
 *  3. Optionally point at the thing that's wrong (brightmark annotate mode) or attach a file.
 *  4. Submit → "Thanks — logged as #NNN."
 *
 * No GitHub wording is shown. The browser sends only non-authoritative client hints; the server
 * derives identity/business, redacts PII, and raises the issue. Built on the app's own Dialog
 * primitive, and tagged `data-brightmark-ui` so the annotate capture excludes its own chrome.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bug,
  Calculator,
  CheckCircle2,
  Lightbulb,
  Loader2,
  MousePointerClick,
  Paperclip,
  Send,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { captureClientHints, submitFeedback, type FeedbackType } from '@/api/feedback'
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'
import { useFeedbackStore } from '@/stores/useFeedbackStore'
import { AnnotationChip } from './annotate/AnnotationChip'

const MAX_DESCRIPTION = 5000
const ALLOWED_MEDIA = 'image/png,image/jpeg,image/webp'

interface FeedbackModalProps {
  open: boolean
  onClose: () => void
  /** Type tile preselected for this entry-point session. */
  initialType?: FeedbackType
  /**
   * Bumped only on a genuine entry-point click (`openFeedback()`) — NOT on every `open` transition,
   * which also flips false→true→false across the host's internal annotate round-trip. The fresh-
   * report reset (type + description + attachment + submit state) fires once per `sessionId`, not
   * once per `open` transition, so re-annotating from inside an already-open modal doesn't wipe what
   * the user already picked.
   */
  sessionId: number
  /** Optional brightmark annotation captured in annotate mode. */
  annotation: BrightmarkAnnotation | null
  /** Element screenshot from annotate mode — arrives asynchronously, after the modal opens. */
  capturedMedia: Blob | null
  /** Enter annotate mode (the host hides this modal while the overlay is up). */
  onStartAnnotate: () => void
  /** Drop the captured annotation + its screenshot. */
  onDiscardAnnotation: () => void
}

interface TypeTile {
  type: FeedbackType
  label: string
  blurb: string
  icon: typeof Bug
}

const TYPE_TILES: TypeTile[] = [
  { type: 'bug', label: "Something's broken", blurb: 'A bug or error', icon: Bug },
  {
    type: 'data_integrity',
    label: 'Numbers look wrong',
    blurb: 'A figure or total seems off',
    icon: Calculator,
  },
  { type: 'suggestion', label: 'I have an idea', blurb: 'A suggestion', icon: Lightbulb },
  { type: 'feature', label: "I'd like a feature", blurb: 'Request something new', icon: Sparkles },
]

export function FeedbackModal({
  open,
  onClose,
  initialType = 'bug',
  sessionId,
  annotation,
  capturedMedia,
  onStartAnnotate,
  onDiscardAnnotation,
}: FeedbackModalProps) {
  const openMyReports = useFeedbackStore((state) => state.openMyReports)
  const [type, setType] = useState<FeedbackType>(initialType)
  const [description, setDescription] = useState('')
  const [pickedFile, setPickedFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [doneRef, setDoneRef] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Reset local state once per fresh `sessionId`, at the point it actually becomes visible —
  // during render (the "adjusting state while rendering" pattern) rather than in an effect, so the
  // form never flashes stale content. Keyed off `sessionId`, not `open`: `open` also toggles across
  // the host's internal annotate round-trip (same session), which must NOT reset the report.
  const [appliedSessionId, setAppliedSessionId] = useState<number | null>(null)
  if (open && appliedSessionId !== sessionId) {
    setAppliedSessionId(sessionId)
    setType(initialType)
    setDescription('')
    setPickedFile(null)
    setError(null)
    setDoneRef(null)
    setSubmitting(false)
  }

  // A user-picked file always wins over the auto-captured element screenshot.
  const media: Blob | null = pickedFile ?? capturedMedia

  const thumbnailUrl = useMemo(() => {
    if (!annotation || !capturedMedia) return null
    return URL.createObjectURL(capturedMedia)
  }, [annotation, capturedMedia])

  useEffect(() => {
    return () => {
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl)
    }
  }, [thumbnailUrl])

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setPickedFile(f)
    setError(null)
  }

  async function handleSubmit() {
    if (!description.trim()) {
      setError('Please describe what happened.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const result = await submitFeedback({
        type,
        description: description.trim(),
        route: window.location.pathname,
        local_time: new Date().toString(),
        client_hints: captureClientHints(),
        media,
        annotation,
      })
      setDoneRef(result.reference)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send your feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const mediaName = pickedFile?.name ?? (capturedMedia ? 'element-screenshot.png' : '')

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-brightmark-ui="modal"
        data-testid="feedback-modal"
        // DialogContent is a grid, so its children default to `min-width: auto` and an
        // unbreakable captured string (a text snippet scraped from the DOM) pushes them past
        // the dialog's own width instead of ellipsising — at 390px that overflowed the viewport.
        // `[&>*]:min-w-0` lets the grid items shrink so the chip's `truncate` can do its job.
        className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-lg [&>*]:min-w-0"
      >
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            {doneRef === null
              ? 'Tell us what went wrong — you can point at the exact thing on screen.'
              : 'Thanks for the report.'}
          </DialogDescription>
        </DialogHeader>

        {doneRef !== null ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="text-success size-12" />
            <p className="text-lg font-semibold">Thanks — logged as #{doneRef}.</p>
            <p className="text-muted-foreground text-sm">
              We&apos;ll take a look. You can close this window.
            </p>
            <Button className="mt-2 min-h-11" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {annotation && (
              <AnnotationChip
                annotation={annotation}
                thumbnailUrl={thumbnailUrl}
                onDiscard={onDiscardAnnotation}
              />
            )}

            {/* Type tiles */}
            <div>
              <p className="mb-2 text-sm font-medium">What&apos;s this about?</p>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_TILES.map((t) => {
                  const Icon = t.icon
                  const active = type === t.type
                  return (
                    <button
                      key={t.type}
                      type="button"
                      onClick={() => setType(t.type)}
                      aria-pressed={active}
                      className={cn(
                        'flex min-h-11 flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
                        active ? 'border-primary bg-primary/10' : 'hover:bg-muted'
                      )}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Icon className="text-primary size-4 shrink-0" />
                        {t.label}
                      </span>
                      <span className="text-muted-foreground text-xs">{t.blurb}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="feedback-description" className="mb-1.5 block text-sm font-medium">
                Tell us more
              </label>
              <Textarea
                id="feedback-description"
                value={description}
                maxLength={MAX_DESCRIPTION}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="What happened, and what did you expect?"
              />
            </div>

            {/* Annotate + attach */}
            <div className="flex flex-wrap gap-2">
              {!annotation && (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  onClick={onStartAnnotate}
                  data-testid="feedback-annotate"
                >
                  <MousePointerClick className="size-4" />
                  Point at the problem
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="size-4" />
                Attach a screenshot
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_MEDIA}
                className="hidden"
                onChange={onPickFile}
              />
            </div>
            {mediaName && (
              <p className="text-muted-foreground truncate text-xs">Attached: {mediaName}</p>
            )}

            {error && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        {doneRef === null && (
          <DialogFooter>
            <Button
              variant="ghost"
              className="min-h-11 sm:mr-auto"
              onClick={() => {
                onClose()
                openMyReports()
              }}
              data-testid="feedback-my-reports"
            >
              My reports
            </Button>
            <Button variant="outline" className="min-h-11" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="min-h-11"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="feedback-submit"
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Send feedback
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
